import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';

export default function HeroSection() {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });

  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 0.6], ['0%', '20%']);

  const handleScrollDown = () => {
    const el = document.querySelector('#countdown');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      ref={containerRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Cinematic background with parallax */}
      <motion.div className="absolute inset-0 z-0" style={{ y: bgY }}>
        <div className="absolute inset-0 bg-gradient-to-b from-midnight via-ink to-midnight-dark" />
        <img
          src="https://readdy.ai/api/search-image?query=Elegant%20dark%20cinematic%20wedding%20atmosphere%20with%20soft%20golden%20bokeh%20lights%20floating%20in%20deep%20navy%20black%20background,%20subtle%20Islamic%20geometric%20patterns%20in%20faint%20gold%20lines,%20luxurious%20and%20romantic%20mood,%20ethereal%20sparkles,%20premium%20film%20intro%20style,%20no%20people,%20abstract%20artistic%20composition"
          alt=""
          className="w-full h-[130%] object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-midnight/60 via-transparent to-midnight-dark" />
        <div className="absolute inset-0 bg-gradient-to-t from-midnight-dark via-transparent to-midnight/40" />
      </motion.div>

      {/* Radial glow center */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-copper/5 blur-[120px] pointer-events-none" />

      {/* Content */}
      <motion.div
        className="relative z-10 text-center px-6 max-w-4xl mx-auto"
        style={{ opacity: contentOpacity, y: contentY }}
      >
        {/* Bismillah - cinematic reveal */}
        <motion.div
          initial={{ opacity: 0, y: 40, filter: 'blur(10px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          transition={{ duration: 1.4, delay: 0.2, ease: 'easeOut' }}
          className="mb-10"
        >
          <p className="font-serif text-xl md:text-2xl text-copper-light italic tracking-wide">
            In the name of Allah,
          </p>
          <p className="font-serif text-base md:text-lg text-copper/60 italic mt-1 tracking-wider">
            The most beneficent & most graceful
          </p>
        </motion.div>

        {/* Invitation line */}
        <motion.p
          initial={{ opacity: 0, letterSpacing: '8px' }}
          animate={{ opacity: 1, letterSpacing: '4px' }}
          transition={{ duration: 1.2, delay: 0.8 }}
          className="font-sans text-[10px] md:text-xs uppercase tracking-[4px] text-pearl/40 mb-12"
        >
          We invite you to celebrate the wedding of
        </motion.p>

        {/* Names - cinematic staggered reveal */}
        <div className="flex flex-col items-center gap-1 md:gap-2 mb-12">
          <motion.h1
            initial={{ opacity: 0, y: 80, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.2, delay: 1.2, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-cream drop-shadow-[0_4px_30px_rgba(198,142,95,0.25)]"
          >
            Sharoof C Majeed
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 1.8, ease: 'backOut' }}
            className="flex items-center gap-6 md:gap-10 my-2 md:my-3"
          >
            <span className="w-16 md:w-28 h-px bg-gradient-to-r from-transparent via-copper/50 to-transparent" />
            <span className="font-serif text-3xl md:text-5xl text-copper italic">&</span>
            <span className="w-16 md:w-28 h-px bg-gradient-to-r from-transparent via-copper/50 to-transparent" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 80, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.2, delay: 2.0, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-cream drop-shadow-[0_4px_30px_rgba(198,142,95,0.25)]"
          >
            Adhabiya Shekkeer Ali
          </motion.h1>
        </div>

        {/* Date reveal */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 2.6 }}
          className="font-sans text-xs md:text-sm uppercase tracking-[5px] text-copper/50 mb-14"
        >
          June 6, 2026
        </motion.p>

        {/* CTA with glow */}
        <motion.button
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 3 }}
          whileHover={{ scale: 1.05, boxShadow: '0 0 40px rgba(198,142,95,0.25)' }}
          whileTap={{ scale: 0.97 }}
          onClick={handleScrollDown}
          className="group relative inline-flex items-center gap-3 px-10 py-4 rounded-full border border-copper/30 bg-copper/5 backdrop-blur-md text-cream font-sans text-xs uppercase tracking-[3px] transition-all duration-500 hover:bg-copper/15 hover:border-copper/50"
        >
          <span className="relative z-10">View Invitation</span>
          <motion.span
            animate={{ y: [0, 4, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            className="relative z-10 w-6 h-6 rounded-full bg-copper/15 flex items-center justify-center group-hover:bg-copper/30 transition-all"
          >
            <i className="ri-arrow-down-line text-copper text-sm w-4 h-4 flex items-center justify-center" />
          </motion.span>
          {/* Glow ring */}
          <div className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-md bg-copper/10" />
        </motion.button>
      </motion.div>

      {/* Bottom fade to next section */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-midnight-dark to-transparent z-[5]" />
    </section>
  );
}